import { _ } from "extensions/underscore";
import levenshtein from "js-levenshtein";
import { Component } from "react";
import { connect } from "react-redux";
import { MarionetteToReact } from "extensions/MarionetteToReact";

import {
    Bloodhound,
    Sources as TypeaheadSources
} from "common/components/typeahead";

import { merchantNameNormalizer } from "common/utils";
import { store } from "common/store";
import { references } from "common/helpers/references";

import { Qualifiers } from "common/components/queryBuilder/qualifiers";
import { QueryBuilder } from "common/components/queryBuilder/queryBuilder";

import { UPDATE_FILTERS, FETCH_TYPEAHEAD } from "common/actions/requests";

import { pivots } from "./pivots";

const MnFiltersBar = QueryBuilder.extend({
    sorter: _.identity,
    
    getInstanceQualifiers() {
        return new Qualifiers({
            value: this.getOption("qualifiers")
        });
    },
    
    buildTypeaheadSources() {
        const dimensions = [
            {
                data: references.getAliasGroupByType("merchant").map(({ id, display_name }) => ({
                    pivot: "merchant_ref",
                    value: id,
                    alias: display_name,
                    normalized_name: merchantNameNormalizer(display_name),
                    typeString: pivots.merchant_ref
                })),
                datumTokenizer: Bloodhound.tokenizers.obj.whitespace(["normalized_name", "alias", "value"])
            },
            
            ...[
                "product",
                "is_mimicked",
                "is_superuser",
                "is_wp_user"
            ].map((pivot) => ({
                data: Object.entries(references.getAliasGroupByType(pivot)).reduce((memo, [key, value]) => [
                    ...memo,
                    {
                        pivot,
                        value: key,
                        alias: value,
                        typeString: pivots[pivot]
                    }
                ], [])
            })),
            
            ...Object.entries(store.getState().requests.typeahead).map(([key, values]) => ({
                data: values.map((value) => ({
                    pivot: "uri_template",
                    value,
                    alias: value,
                    typeString: pivots.uri_template
                }))
            }))
        ];
        
        // This variable holds all Sources that will be passed to Typeahead component
        const sourcesCollection = new TypeaheadSources();
        const defaultQueryTokenizer = (query) => {
            // Remove first character if it is a minus sign
            let q = query;
            const hasMinusAsFirstChar = query[0] === "-";
            this.excludeNextTokenFlag = hasMinusAsFirstChar;
            this.$el.toggleClass("next-token-exclude", this.excludeNextTokenFlag).toggleClass("next-token-include", !this.excludeNextTokenFlag);
            if (hasMinusAsFirstChar) {
                q = query.substr(1);
            }
            return Bloodhound.tokenizers.whitespace(q);
        };
    
        dimensions.forEach(({ data, display = "alias", datumTokenizer = Bloodhound.tokenizers.obj.whitespace(display), queryTokenizer = defaultQueryTokenizer }) => {
            sourcesCollection.add({
                display,
                source: new Bloodhound({
                    datumTokenizer,
                    queryTokenizer,
                    local: data,
                    identify: (obj) => obj.value,
                    sufficient: 100,
                    sorter: (a, b) => {
                        // It sucks that Bloodhound doesn't keep a reference to search query inside it's instance.
                        // We should consider rewrite it's search method if find this is a performance bottleneck
                        const input_string = this.tokensInput.el.querySelector(".tt-input").value;
                        return levenshtein(a[display], input_string) - levenshtein(b[display], input_string);
                    }
                }),
                limit: 100,
                templates_suggestion: ({ alias }) => `<div>${alias}</div>`
            });
        });
        
        return sourcesCollection;
    },
    
    setCollection() {
        return store.dispatch(FETCH_TYPEAHEAD()).then(() => {
            this.collection = this.buildTypeaheadSources();
        });
    },
    
    onUpdatedInput(qualifiers) {
        this.getOption("callbackHandler")("UPDATE_FILTERS", { qualifiers });
    }
});

const mapStateToProps = (state) => ({
    qualifiers: state.requests.qualifiers
});

const mapDispatchToProps = {
    UPDATE_FILTERS
};

class FiltersBar extends Component {
    shouldComponentUpdate(nextProps) {
        // Do not re-render the view if qualifiers haven't changed
        return this.props.qualifiers !== nextProps.qualifiers;
    }
    
    callbackHandler = (type, data) => {
        if (type === "UPDATE_FILTERS") {
            this.props.UPDATE_FILTERS(data);
        }
    };
    
    render() {
        return (
            <MarionetteToReact
                View={MnFiltersBar}
                savedFilters={false}
                qualifiers={this.props.qualifiers}
                className="filters"
                callbackHandler={this.callbackHandler}
            />
        );
    }
}

export const FiltersBarRedux = connect(mapStateToProps, mapDispatchToProps)(FiltersBar);
